import React, { useContext, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import PlaceSearch from "../components/PlaceSearch";
import PostcodeInput from "../components/PostcodeInput";
import AttributionDialog from "../components/AttributionDialog";
import { BUY_REPORT_PATH, REPORT_PRICES, SIGNUP_PATH, SUBSCRIBE_PATH, SUB_PLANS, TOPUP }
    from "../account/accountRules";
import { AI_HOOK, AI_TAGLINE, AI_TICKS } from "../account/pricingCopy";
import { FREE_LAYER_SWITCHES, FREE_SEARCHES } from "../account/mapAccessRules";
import { homesPath, normalisePostcode } from "../property/propertyValue";
import { LISTING_PATH, extractListingUrl, isListingUrl, linkSaysRent } from "../account/listingRules";
import BigPictureStrip from "./BigPictureStrip";
import { BIG_PICTURE_PATH, useLandingReturn } from "./landingScroll";
import { AREA_PAGES_PATH } from "../Layers/areaPageLink";
import SiteNav from "./SiteNav";
import SampleLinks from "../account/SampleLinks";
import Tier, { OwnedTiersContext } from "./Tier";
import useCurrentUser from "../account/useCurrentUser";
import useListingAccess from "../account/useListingAccess";
import useWantsRent from "../account/useWantsRent";
import { openCookieSettings } from "../consent";
import { COOKIES_PATH, FAQ_PATH, PRIVACY_PATH, TERMS_PATH } from "../legal/legalPaths";
import './Landing.css';

export const MAP_PATH = '/map';

// The plans (prices from accountRules.js)
const [STANDARD_PLAN, PLUS_PLAN] = SUB_PLANS;

export const useIsNarrow = () => {
    const query = '(max-width:640px)';
    const [narrow, setNarrow] = useState(() => !!(window.matchMedia && window.matchMedia(query).matches));
    useEffect(() => {
        if (!window.matchMedia) return;
        const mq = window.matchMedia(query);
        const onChange = (e) => setNarrow(e.matches);
        mq.addEventListener('change', onChange);
        return () => mq.removeEventListener('change', onChange);
    }, []);
    return narrow;
};

/* ---------- hero ---------- */

// The hero's ways in, chosen with radio buttons. Exploring the map needs no search, so it's its
// own button under the search (MapButton), not one of these
const SEARCH_MODES = [
    {key:'any', label:'Search', chip:'One search box', tier:'open',
        text:'Type a postcode, street or area, or paste a Rightmove or Zoopla link, and pick what you want from there.'},
    {key:'home', label:'I want to look up a property', chip:'Look up a property', tier:'account',
        text:'See what a home is worth and sold for, how energy efficient it is, and what nearby homes went for.', signUp:' Sign up is free.'},
    {key:'report', label:'I want a full property report', chip:'Full property report', tier:'sub',
        text:'A full report on a home to buy or rent: from its Rightmove or Zoopla listing, or a home you pick yourself, with the area around it.'},
    {key:'areareport', label:'I want an area report', chip:'Area report', tier:'sub',
        text:'Prices, rents and crime against the council, region and country, with their graphs, the nearest stops and shops, who lives there, noise and flood risk.'},
];

// The area report page, built by its own module (areareport/AreaReport.js), opened at a point
export const AREA_REPORT_LINK = '/area-report';
export const areaReportLink = (place) => AREA_REPORT_LINK + '?' + new URLSearchParams({
    x:String(Math.round(Number(place.x))), y:String(Math.round(Number(place.y))), ...(place.name ? {n:place.name} : {})});

// A row of small choices under the full report option: to buy or rent, and how to find the home
const SubChoice = ({name, value, options, onChange, label}) => (
    <div className="subopts" role="radiogroup" aria-label={label}>
        {options.map(([v, text]) => (
            <label key={v} className={'subopt' + (v === value ? ' on' : '')}>
                <input type="radio" name={name} value={v} checked={v === value} onChange={() => onChange(v)}/>{text}
            </label>
        ))}
    </div>
);

// The landing page's search, also on the Property lookup page (/property) and the Buy property
// report page (/buy). Needs an OwnedTiersContext provider above it (useOwnedTiers) for what the
// user may use. onPostcode: told what is typed in the postcode box, for the page's own map finder.
// fixedMode: one mode only ('report' or 'areareport'), with no mode chips; the full report then
// offers both ways to find the home to anyone, and starts on To rent for a renter's profile.
export const HeroSearch = ({isNarrow, onPostcode, fixedMode = null}) => {
    const navigate = useNavigate();
    // The mode chosen; until then logged in users get the one search box. Logged out visitors only
    // enter a postcode: they get Look up a property and the full report, found by postcode
    const [chosen, setMode] = useState(null);
    // The place picked in the one search box, waiting for what to do with it
    const [picked, setPicked] = useState(null);
    const [postcode, setPostcode] = useState('');
    const [url, setUrl] = useState('');
    const [error, setError] = useState('');
    // The full report: a home to buy or rent, found from its listing link or by its postcode
    const [chosenDeal, setDeal] = useState(null);
    const [chosenWay, setWay] = useState('find');
    const owned = useContext(OwnedTiersContext);
    const signedIn = !!(owned && owned.account);
    // Logged out visitors only look up a property; the home's page then offers free details or the paid report
    const POSTCODE_MODES = ['home'];
    const mode = fixedMode || (signedIn ? (chosen || 'any') : (POSTCODE_MODES.includes(chosen) ? chosen : 'home'));
    const bothWays = signedIn || !!fixedMode;
    const way = bothWays ? chosenWay : 'find';
    const current = SEARCH_MODES.find((m) => m.key === mode);
    // Whether the logged in user's profile says they're looking to rent, to put the renter's report
    // first when a pasted link doesn't say which it is
    const wantsRent = useWantsRent();
    const deal = chosenDeal || (fixedMode && wantsRent ? 'rent' : 'buy');

    const typed = () => {
        if (!postcode.trim()) { setError('Enter a postcode, like N5 1XL.'); return null; }
        const pc = normalisePostcode(postcode);
        if (!pc) { setError("That isn't a full UK postcode. Check it has both parts, like N5 1XL."); return null; }
        return pc;
    };
    const findHomes = (e) => {
        e.preventDefault();
        // Logged out visitors can paste a listing link here too: its full report's offer (ListingLookup.js)
        if (!signedIn) {
            const link = extractListingUrl(postcode);
            if (isListingUrl(link)) {
                const says = linkSaysRent(link);
                navigate(LISTING_PATH + '?' + new URLSearchParams({...(says === true ? {type:'rent'} : {}), url:link}));
                return;
            }
            if (/^(https?:\/\/|www\.)/i.test(postcode.trim())) {
                setError("That link isn't a Rightmove or Zoopla listing. Copy it from the property's page, like rightmove.co.uk/properties/123456789.");
                return;
            }
        }
        const pc = typed();
        if (pc) navigate(homesPath(pc));
    };
    // A postcode picked from the suggestions goes straight to its homes
    const pickPostcode = (pc) => { setPostcode(pc); navigate(homesPath(pc)); };
    // The listing page starts the lookup, or lists the postcode's homes, once it knows the user can use it
    const reportPath = (params) => LISTING_PATH + '?' + new URLSearchParams({...(deal === 'rent' ? {type:'rent'} : {}), ...params});
    const readListing = (e) => {
        e.preventDefault();
        if (!url.trim()) { setError(`Paste the link to a Rightmove or Zoopla listing of a home ${deal === 'rent' ? 'to rent' : 'for sale'}.`); return; }
        if (!isListingUrl(url)) { setError('That isn\'t a Rightmove or Zoopla listing link. Copy it from the property\'s page, like rightmove.co.uk/properties/123456789.'); return; }
        if (linkSaysRent(url) != null && linkSaysRent(url) !== (deal === 'rent')) {
            setError(deal === 'rent' ? 'That link is for a home for sale. Choose To buy, or paste a link to a home to rent.'
                : 'That link is for a home to rent. Choose To rent, or paste a link to a home for sale.');
            return;
        }
        navigate(reportPath({url:url.trim()}));
    };
    const findForReport = (e) => {
        e.preventDefault();
        const pc = typed();
        if (pc) navigate(reportPath({postcode:pc}));
    };
    const pickReportPostcode = (pc) => { setPostcode(pc); navigate(reportPath({postcode:pc})); };
    const openAreaReport = (item) => {
        const p = item && item.place;
        if (p && p.x != null) navigate(areaReportLink(p));
    };
    const choose = (key) => { setMode(key); setError(''); setPicked(null); };

    const openListing = (link, rent) => navigate(LISTING_PATH + '?' + new URLSearchParams({...(rent ? {type:'rent'} : {}), url:link}));

    // The one search box: a listing link goes to its full report (to rent when the link says so),
    // a full postcode to its homes, and anything else to the place suggestions
    const searchText = (text) => {
        const link = extractListingUrl(text);
        if (isListingUrl(link)) {
            // Enter takes the first row: what the link says, else what the profile says they want
            const says = linkSaysRent(link);
            openListing(link, says === null ? wantsRent : says);
            return true;
        }
        if (/^(https?:\/\/|www\.)/i.test(text.trim())) {
            setError("That link isn't a Rightmove or Zoopla listing. Copy it from the property's page, like rightmove.co.uk/properties/123456789.");
            return true;
        }
        const pc = normalisePostcode(text);
        if (pc) { navigate(homesPath(pc)); return true; }
        return false;
    };
    // A pasted link gets rows saying what it will do, in place of place matches: the one report
    // the link says it is for, or, when the link doesn't say, both, the kind the user's profile
    // says they're looking for (buy or rent) first
    const linkAction = (text) => {
        const link = extractListingUrl(text);
        if (isListingUrl(link)) {
            const from = 'From its ' + (/zoopla/i.test(link) ? 'Zoopla' : 'Rightmove') + ' listing. We read the listing and check the area around it.';
            const says = linkSaysRent(link);
            const buyRow = {label:"Get the buyer's report on this home", sub:says === null ? 'If it is for sale. ' + from : from, run:() => openListing(link, false)};
            const rentRow = {label:"Get the renter's report on this home", sub:says === null ? 'If it is to rent. ' + from : from, run:() => openListing(link, true)};
            if (says !== null) return [says ? rentRow : buyRow];
            return wantsRent ? [rentRow, buyRow] : [buyRow, rentRow];
        }
        if (/^(https?:\/\/|www\.)/i.test(text.trim())) {
            return {label:"That link isn't a Rightmove or Zoopla listing", sub:"Copy the link from the property's own page, like rightmove.co.uk/properties/123456789"};
        }
        return null;
    };
    // A postcode goes straight to its addresses, which offer its area report and the map at the top;
    // any other place waits for what to do with it
    const pickAny = (item) => {
        const p = item && item.place;
        if (!p || p.x == null) return;
        const pc = normalisePostcode(p.name || '');
        if (pc) { navigate(homesPath(pc), {state:{place:{name:pc, x:p.x, y:p.y}}}); return; }
        setPicked(p);
        setError('');
    };
    const pickedPostcode = picked && normalisePostcode(picked.name || '');
    const pickedMap = picked && MAP_PATH + '?' + new URLSearchParams({x:String(Math.round(Number(picked.x))), y:String(Math.round(Number(picked.y))),
        d:'800', s:'pointCircle', ...(picked.name ? {n:picked.name} : {})});
    // Logged out, looking up a property, the box also takes a Rightmove or Zoopla link (findHomes)
    const postcodeOnly = signedIn || mode !== 'home';
    const postcodeBox = (onPick) => (
        <PostcodeInput className="pc" id="lp-postcode" aria-label={postcodeOnly ? 'Postcode' : 'Postcode or listing link'} value={postcode} onPick={onPick}
            placeholder={postcodeOnly ? 'Enter a postcode, e.g. N5 1XL' : isNarrow ? 'Postcode or listing link' : 'Enter a postcode, or paste a Rightmove or Zoopla link'}
            aria-invalid={!!error} aria-describedby={error ? 'lp-search-err' : undefined}
            onChange={(e) => { setPostcode(e.target.value); setError(''); if (onPostcode) onPostcode(e.target.value); }}/>
    );

    return (
        <>
            {mode==='any' &&
                <div className="search">
                    <div className="in">
                        <PlaceSearch detached isMobile={isNarrow} showLocation={false} onChoose={pickAny} onEnterText={searchText} textAction={linkAction}
                            placeholder={isNarrow ? 'Postcode, area or listing link' : 'Postcode, street, area, or a Rightmove or Zoopla link'}/>
                    </div>
                </div>}
            {mode==='any' && picked &&
                <div className="picked" role="group" aria-label={'What to see for ' + (picked.name || 'this place')}>
                    <b>{picked.name || 'The place you picked'}</b>
                    <div className="picked-acts">
                        {pickedPostcode && <Link className="btn b-blue" to={homesPath(pickedPostcode)}>See the homes here</Link>}
                        <Link className={'btn ' + (pickedPostcode ? 'b-ghost' : 'b-blue')} to={areaReportLink(picked)}>Area report</Link>
                        <Link className="btn b-ghost" to={pickedMap}>Open on the map</Link>
                    </div>
                    {pickedPostcode
                        ? <small>Pick the home from the list to see its details or start its full report. Don't know which one it is? The list lets you carry on with just the postcode.</small>
                        : <small>The area report works from a rough location.</small>}
                </div>}
            {signedIn && !fixedMode && <fieldset className="modes chips">
                <legend>{mode === 'any' ? 'Or go straight to' : 'What do you want to do?'}</legend>
                {SEARCH_MODES.filter((m) => signedIn ? m.key !== 'any' || mode !== 'any' : POSTCODE_MODES.includes(m.key)).map((m) => (
                    <label key={m.key} className={'mode' + (m.key === mode ? ' on' : '')}>
                        <input type="radio" name="lp-mode" value={m.key} checked={m.key === mode} onChange={() => choose(m.key)}/>
                        <span>{m.key === 'any' ? '‹ Back to one search box' : m.chip}</span>
                    </label>
                ))}
            </fieldset>}
            {mode==='home' &&
                <form className="search" onSubmit={findHomes} noValidate>
                    <div className="in">{postcodeBox(pickPostcode)}</div>
                    <button type="submit" className="go">{!signedIn && isListingUrl(extractListingUrl(postcode)) ? 'Get the report' : 'Find homes'}</button>
                </form>}
            {mode==='report' && <>
                <div className="subrow">
                    <SubChoice name="lp-deal" label="To buy or rent" value={deal} onChange={(v) => { setDeal(v); setError(''); }}
                        options={[['buy', 'To buy'], ['rent', 'To rent']]}/>
                    {bothWays && <SubChoice name="lp-way" label="How to find the home" value={way} onChange={(v) => { setWay(v); setError(''); }}
                        options={[['find', "I'll find the home myself"], ['link', 'I have a Rightmove or Zoopla link']]}/>}
                </div>
                {way === 'link' ?
                    <form className="search" onSubmit={readListing} noValidate>
                        <div className="in">
                            <input type="url" inputMode="url" className="pc" id="lp-listing" aria-label="Rightmove or Zoopla link" value={url}
                                autoComplete="off" spellCheck={false} placeholder={`Paste a Rightmove or Zoopla link to a home ${deal === 'rent' ? 'to rent' : 'for sale'}`}
                                aria-invalid={!!error} aria-describedby={error ? 'lp-search-err' : undefined}
                                onChange={(e) => { setUrl(extractListingUrl(e.target.value)); setError(''); }}/>
                        </div>
                        <button type="submit" className="go">Get the report</button>
                    </form>
                : <form className="search" onSubmit={findForReport} noValidate>
                        <div className="in">{postcodeBox(pickReportPostcode)}</div>
                        <button type="submit" className="go">Start my report</button>
                    </form>}
            </>}
            {mode==='areareport' &&
                <div className="search">
                    <div className="in">
                        {/* As the map's search, but the place picked opens the area report, not the map */}
                        <PlaceSearch detached isMobile={isNarrow} showLocation={false} onChoose={openAreaReport}
                            placeholder={'Enter a postcode, street or area'+(isNarrow ? '' : ', e.g. Upper Street or Islington')}/>
                    </div>
                </div>}
            {error && <p className="err" id="lp-search-err">{error}</p>}
            {fixedMode ? <div className="modenote"><span>{current.text}</span></div> : signedIn
                ? <div className="modenote"><Tier tier={current.tier} icon hideOwned/><span>{current.text}</span></div>
                : <div className="modenote"><span>Enter a postcode and pick the home to see its value, sales and energy rating free with an
                    account, or paste its Rightmove or Zoopla link. Get the full report on it and its area, with an offer guide, for {REPORT_PRICES.buy}.</span></div>}
        </>
    );
};

// Under the hero's search: the map needs no search at all, so it gets its own way in
const MapButton = () => (
    <>
        <div className="or" aria-hidden="true"><span/>or<span/></div>
        <Link className="btn b-map" to={MAP_PATH}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                strokeLinejoin="round" aria-hidden="true"><path d="M9 4 3 6v14l6-2 6 2 6-2V4l-6 2-6-2z"/><path d="M9 4v14M15 6v14"/></svg>
            Explore the map
        </Link>
    </>
);

/* ---------- the data, and the questions it answers ---------- */

// Counts from the database, as pricingCopy.js has them (checked 2026-10-27), plus the crime records
const FACTS = [
    ['13 million', 'sales since 2012'],
    ['30 million', 'energy certificates'],
    ['27 million', 'street crime records'],
    ['1.6 million', 'postcodes'],
    ['190,000', 'neighbourhoods, each with its census profile'],
];

const FactsStrip = () => (
    <section className="facts" aria-labelledby="lp-facts">
        <div className="wrap">
            <div className="facts-row">
                {FACTS.map(([n, t]) => <div key={t}><b>{n}</b><span>{t}</span></div>)}
            </div>
            <h2 id="lp-facts">Data is easy to find. Joining it up is the clever bit.</h2>
            <p>Every sale, energy certificate and crime is pinned to its street, then matched with who lives there: ages, households, jobs,
                qualifications and how people get to work. So one search answers what used to take fourteen tabs and a spreadsheet.</p>
        </div>
    </section>
);

// The three AI runs (pricingCopy.js AI_RUNS), each led by the question it answers
const QUESTIONS = [
    ['"Is that asking price fair?"', <>The <b>offer guide</b> weighs it against real sales nearby and everything we can find online, then tells you
        what to offer, with the evidence to back it up.</>],
    ['"What\'s it like to live there?"', <>The <b>area guide</b> covers crime, schools, shops, the walk to the station, and the road noise that
        was oddly quiet on viewing day.</>],
    ['"What\'s the listing leaving out?"', <>Paste a Rightmove or Zoopla link and the <b>link check</b> digs up what the ad leaves out: price
        history, size, tenure and energy rating, with short leases and shared ownership flagged.</>],
];

const Questions = () => (
    <section className="wrap block qs" aria-labelledby="lp-qs">
        <div className="shead">
            <span className="sign-label">THE BIG QUESTIONS <span>AI</span></span>
            <h2 id="lp-qs">Three things the viewing won't tell you</h2>
        </div>
        <div className="q-row">
            {QUESTIONS.map(([q, a]) => <div className="q" key={q}><h3>{q}</h3><p>{a}</p></div>)}
        </div>
    </section>
);

/* ---------- have a nose around: what each tier gets ---------- */

// The three cards under "Have a nose around first", one per tier (Tier.js), as the map's
// access rules (account/mapAccessRules.js) and the plans (accountRules.js) set them
const NOSE_CARDS = [
    {tier:'open', h:'Just looking', p:'No sign-up, no email, no awkward small talk.',
        items:[['The Big Picture.', 'Where prices are rising, falling or just sulking, for every council and every neighbourhood in it.'],
            ['A taste of the map.', 'Try ' + FREE_LAYER_SWITCHES + ' layers and ' + FREE_SEARCHES + ' places before we ask your name.'],
            ['Sample reports.', "See exactly what you'd be paying for."]],
        cta:() => <>
            <Link className="btn b-green" to={BIG_PICTURE_PATH}>Open the Big Picture</Link>
            {/* The static area pages, outside the app */}
            <a className="way-link" href={AREA_PAGES_PATH}>Browse areas</a>
        </>},
    {tier:'account', h:'Sign up, still free', p:"An email and a password. That's the whole price.",
        items:[['The whole map.', 'Sold prices, crime, schools, stations, shops, air and noise, street by street. Zoom in until you can almost see the bins.'],
            ['Property lookup.', 'Any home\'s estimated value, sale history and energy rating. Nosy? We prefer "well informed".']],
        cta:(owned) => owned && owned.account
            ? <Link className="btn b-blue" to={MAP_PATH}>Open the map</Link>
            : <Link className="btn b-blue" to={SIGNUP_PATH}>Sign up free</Link>},
    {tier:'sub', dark:true, h:'The full works', p:'From ' + STANDARD_PLAN.price + ' a month, or a single report from ' + REPORT_PRICES.rent + '.',
        items:[['Link checks.', 'Paste a Rightmove or Zoopla link and we dig up what the listing left out.'],
            ["Full buyer's report.", 'All the details behind the estimated value, an area report thrown in, and an AI researched offer guide '
                + 'telling you what to offer and why.'],
            ['Area report.', 'Local prices, rents, crime, transport, amenities, demographics, schools, noise and flood risk.'],
            ["The map's full area overview,", 'with its price and crime graphs.']],
        cta:() => <a className="btn b-white" href="#plans">See the plans</a>},
];

/* ---------- plans ---------- */

// The plans, as the map's access rules (account/mapAccessRules.js) and pricing set them: the map is free with
// an account, and the paid plans sell the AI reports, in the words of the plans box (AI_TICKS)
const PLANS = [
    {tier:'once', h:'Single report', prices:[[REPORT_PRICES.buy, 'for buyers'], [REPORT_PRICES.rent, 'for renters']], to:BUY_REPORT_PATH,
        btn:'Buy a report', cls:'b-ghost',
        items:[['inc', 'Everything in Free'], ...AI_TICKS.report, 'Unlocked for 60 days, emailed to you with a PDF (renters get the area guide)']},
    {tier:'sub', h:STANDARD_PLAN.name, price:STANDARD_PLAN.price, per:'a month, cancel any time', to:SUBSCRIBE_PATH + '?plan=standard',
        btn:'Subscribe now', cls:'b-ink', hi:true,
        items:[['inc', 'Everything in Free'], ...AI_TICKS.plan(STANDARD_PLAN),
            "The map's full area overview, and its price and crime graphs", 'Area reports, and PDFs of every report']},
    {tier:'sub', h:PLUS_PLAN.name, price:PLUS_PLAN.price, per:'a month, cancel any time', to:SUBSCRIBE_PATH + '?plan=plus',
        btn:'Subscribe to Plus', cls:'b-ink',
        items:[['inc', 'Everything in Standard'], ...AI_TICKS.plan(PLUS_PLAN), 'For buyers viewing lots of homes']},
];

const PlanButton = ({plan}) => plan.to.startsWith('#')
    ? <a className={'btn '+plan.cls} href={plan.to}>{plan.btn}</a>
    : <Link className={'btn '+plan.cls} to={plan.to}>{plan.btn}</Link>;

/* ---------- the page ---------- */


// What the user already has, as {account, sub}, for OwnedTiersContext
export const useOwnedTiers = () => {
    const user = useCurrentUser();
    const access = useListingAccess();
    const signedIn = !!(user && user.signedin);
    return {account:signedIn, sub:!!(signedIn && access && access.subscribed)};
};

const Landing = () => {
    const isNarrow = useIsNarrow();
    // Back from a Big Picture map, to where the page was left
    useLandingReturn();
    // A section asked for from another page's top menu (/#plans). The Big Picture was a
    // section here once (/#big-picture), and is now its own page
    const {hash} = useLocation();
    const navigate = useNavigate();
    useEffect(() => {
        if (!hash) return;
        if (hash === '#big-picture') { navigate(BIG_PICTURE_PATH, {replace:true}); return; }
        const el = document.getElementById(hash.slice(1));
        if (el) el.scrollIntoView();
    }, [hash, navigate]);
    const [attributionDialogOpen, setAttributionDialogOpen] = useState(false);
    // Tier pills for what the user already has are left out
    const owned = useOwnedTiers();

    return (
        <OwnedTiersContext.Provider value={owned}>
        <div className="lp home" id="top">
            <SiteNav isNarrow={isNarrow}/>

            <main>
                <section className="hero wrap">
                    <div className="hero-copy">
                        <span className="eyebrow">Every street in England and Wales</span>
                        <h1>The listing shows you the house. <span>We show you the street.</span></h1>
                        <p className="sub">Sold prices, crime, schools, stations, noise and flood risk for the few streets around a home, not the
                            whole town. Then an AI offer guide that tells you what to pay and why.</p>
                        <HeroSearch isNarrow={isNarrow}/>
                        <MapButton/>
                    </div>
                </section>

                <BigPictureStrip/>

                <FactsStrip/>

                <Questions/>

                <section className="wrap block free" aria-labelledby="lp-free">
                    <div className="shead">
                        <span className="sign-label">START HERE <span>FREE</span></span>
                        <h2 id="lp-free">Have a nose around first. Most of it's free.</h2>
                    </div>
                    <div className="ways">
                        {NOSE_CARDS.map((card) => (
                            <div className={card.dark ? 'way dark' : 'way'} key={card.tier}>
                                <Tier tier={card.tier}/>
                                <h3>{card.h}</h3>
                                <p>{card.p}</p>
                                <ul>{card.items.map(([b, text]) => <li key={b}><span><b>{b}</b> {text}</span></li>)}</ul>
                                {card.cta(owned)}
                            </div>
                        ))}
                    </div>
                </section>

                <section className="wrap block" id="plans">
                    <div className="shead">
                        <span className="sign-label">PLANS <span>£</span></span>
                        <h2>Less than the survey. Rather more useful.</h2>
                        <p className="lead"><b>{AI_HOOK}</b> {AI_TAGLINE}</p>
                    </div>
                    <div className="plans">
                        {PLANS.map((plan) => (
                            <div className={plan.hi ? 'plan hi' : 'plan'} key={plan.h}>
                                <Tier tier={plan.tier}/>
                                <h3>{plan.h}</h3>
                                {plan.prices
                                    ? <div className="prices">{plan.prices.map(([pr, who]) => <div className="price" key={who}><b>{pr}</b><span>{who}</span></div>)}</div>
                                    : <div className="price"><b>{plan.price}</b>{plan.per && <span>{plan.per}</span>}</div>}
                                <ul>{plan.items.map((it) => Array.isArray(it)
                                    ? <li key={it[1]} className="inc">{it[1]}</li> : <li key={it}>{it}</li>)}</ul>
                                <PlanButton plan={plan}/>
                                {plan.hi && <SampleLinks className="plan-samples"/>}
                            </div>
                        ))}
                    </div>
                    <p className="plan-small">No account? Open any shared map and try {FREE_LAYER_SWITCHES} of its layers before signing up.
                        Monthly credits start again each month and don't carry over. Run out? A
                        top-up pack adds {TOPUP.credits} credits for {TOPUP.price}.
                        Questions about credits, cancelling or refunds? <Link to={FAQ_PATH + '#plans'}>Read the FAQ</Link>.</p>
                </section>

                <section className="end wrap">
                    <h2>A viewing lasts twenty minutes. <span>A mortgage lasts twenty-five years.</span></h2>
                    <p className="lead">Spend five of those minutes here first.</p>
                    <div className="row">
                        <a className="btn b-blue" href="#top">Check a street</a>
                        <Link className="btn b-ghost" to={MAP_PATH}>Open the map</Link>
                    </div>
                </section>
            </main>

            <footer><div className="wrap">
                <button type="button" onClick={() => setAttributionDialogOpen(true)}>Data sources</button>
                <button type="button" onClick={openCookieSettings}>Cookie settings</button>
                <Link to={FAQ_PATH}>FAQ</Link>
                <Link to={TERMS_PATH}>Terms</Link>
                <Link to={PRIVACY_PATH}>Privacy Policy</Link>
                <Link to={COOKIES_PATH}>Cookie Policy</Link>
                <span>PROPSTATS LTD, trading as trackmystreet. Registered in England and Wales, company number 17556178. Registered office: Unit 13 Freeland Park, Wareham Road, Lytchett Matravers, Poole, BH16 6FA.</span>
            </div></footer>
            <AttributionDialog attributionDialogOpen={attributionDialogOpen} setAttributionDialogOpen={setAttributionDialogOpen}/>
        </div>
        </OwnedTiersContext.Provider>
    );
};

export default Landing;
